import React, { useEffect } from 'react'

import {
  HeadingSmall,
  BodyText,
  Label,
  Loader,
  OrderListItem,
  ChevronRightIcon,
  CTALink,
} from '@pb/components'
import { HeadingLarge } from '@hm-group/fabric-components'
import { useMediaQuery, Breakpoints, getLabelFromStorage } from '@pb/utils'
import cn from 'classnames'
import { useSelector, useDispatch } from 'react-redux'
import { Link } from 'react-router-dom'

import { EXTERNAL_ROUTES, ACCOUNT_ROUTES } from 'App/constants'
import appSelectors from 'App/duck/selectors'
import optimizelyConstants from 'constants/optimizely'
import useOptimizelyDecision, {
  useNorthstarABTestEnabled,
} from 'features/account/hooks/useOptimizelyDecision'
import useOptimizelyToggle from 'features/account/hooks/useOptimizelyToggle'
import OrderItemNS from 'features/account/purchases/components/OnlineOrdersList/components/OrderItemNS'
import { trackOptimizelyEvent } from 'helpers/optimizely'
import TrackVisibilityOnMobile from 'helpers/TrackVisibilityOnMobile'

import styles from './HubPurchaseList.scss'
import {
  operationFetchOnlineOrders,
  operationFetchStoreOrders,
  operationFetchOrders,
} from '../../duck/operations'
import selectors from '../../duck/selectors'
import { getStoreStreetName } from '../../helpers/purchases'
import HubPurchasesSingleMessage from '../HubPurchasesSingleMessage'

const HubPurchasesComponent: React.FC<any> = () => {
  const isAllOrdersLoadingFailed = useSelector(selectors.getIsOrdersLoadingFailed)
  const isUserFailed = useSelector(appSelectors.getIsUserLoadingFailed)
  const isStoreOrdersLoadingFailed = useSelector(selectors.getIsStoreOrdersLoadingFailed)
  const isOnlineOrdersLoadingFailed = useSelector(selectors.getIsOnlineOrdersLoadingFailed)
  const shouldFetchOnlineOrders = useSelector(selectors.getShouldFetchOnlineOrders)
  const shouldFetchStoreOrders = useSelector(selectors.getShouldFetchStoreOrders)
  const isUserFullMember = useSelector(appSelectors.getUserIsFullMember)
  const orderList = useSelector(selectors.getHubOrderList)
  const filteredHubOrderList = useSelector(selectors.getFilteredHubOrderList)
  const isOrdersResultEmpty = useSelector(selectors.getIsOrdersResultEmpty)
  const shouldShowLoader = useSelector(selectors.getShouldShowLoader)
  const isMediumScreen = useMediaQuery(Breakpoints.MEDIUM)
  const payLaterLabel = getLabelFromStorage('omnicredit.optin.title')

  const dispatch = useDispatch()

  const fetchOnlineOrders = (): any => dispatch(operationFetchOnlineOrders())
  const fetchStoreOrders = (): any => dispatch(operationFetchStoreOrders())
  const fetchOrders = (): any => dispatch(operationFetchOrders())

  const isOnlineandStoreOrderEnabled = useOptimizelyToggle({
    key: optimizelyConstants.ACCOUNT_ONLINE_STORE_ORDER,
    internalToggle: 'online_store_order_toggle',
  })

  const isNorthstarBrand = useOptimizelyDecision(optimizelyConstants.ACCOUNT_NORTHSTAR_VARIATION)

  const isNorthStarABtestEnabled = useNorthstarABTestEnabled(
    optimizelyConstants.ACCOUNT_NORTHSTAR_VARIATION,
  )

  const hubOrderList = !isNorthstarBrand ? orderList : filteredHubOrderList

  useEffect(() => {
    if (shouldFetchOnlineOrders && shouldFetchStoreOrders && isOnlineandStoreOrderEnabled) {
      fetchOrders()
    } else {
      if (shouldFetchOnlineOrders) {
        fetchOnlineOrders()
      }
      if (shouldFetchStoreOrders) {
        fetchStoreOrders()
      }
    }
  }, [shouldFetchOnlineOrders, shouldFetchStoreOrders])

  const trackOrderItemClick = (): void => {
    if (isNorthStarABtestEnabled) {
      trackOptimizelyEvent(optimizelyConstants.ACCOUNT_ORDER_CLICK_LANDING_PAGE)
    }
  }

  const renderOrders = (): any => {
    if (shouldShowLoader && !isUserFailed) {
      return <Loader />
    }
    if (isAllOrdersLoadingFailed || (!isUserFullMember && isOnlineOrdersLoadingFailed)) {
      return (
        <HubPurchasesSingleMessage>
          <Label copyKey="hub.purchase.not.loaded" />
        </HubPurchasesSingleMessage>
      )
    }

    if (isOrdersResultEmpty) {
      return (
        <HubPurchasesSingleMessage shouldHaveIcon>
          <Label copyKey="hub.nopurchases.made.title" />
        </HubPurchasesSingleMessage>
      )
    }

    return (
      <ul className={styles.hubOrdersList}>
        {hubOrderList.map(
          (
            {
              orderId,
              itemNumber,
              date,
              totalAmount,
              statusDisplay,
              isCancelled,
              productImages,
              isPayLater,
              orderType,
              storeInfo,
            },
            index,
          ) =>
            isNorthstarBrand ? (
              index < 2 && (
                <OrderItemNS
                  key={orderId}
                  orderRoute={`${ACCOUNT_ROUTES.OnlineOrderDetails.path}/${orderId}`}
                  orderMetaData={getLabelFromStorage('commerce_filter_itemsText', {
                    replacements: itemNumber,
                  })}
                  orderDate={date}
                  orderAmount={totalAmount}
                  orderStatus={statusDisplay}
                  orderImages={productImages}
                  isOrderCancelled={isCancelled}
                  onClick={trackOrderItemClick}
                />
              )
            ) : (
              <OrderListItem
                Tag="li"
                key={orderId}
                orderMetaData={
                  orderType === 'online' ? (
                    <Label copyKey="commerce_filter_itemsText" replacements={itemNumber} />
                  ) : (
                    getStoreStreetName(storeInfo)
                  )
                }
                orderDate={date}
                orderAmount={totalAmount}
                orderStatusDisplay={statusDisplay}
                isOrderCancelled={isCancelled}
                payLaterLabel={isPayLater ? payLaterLabel : undefined}
                orderDetailRoute={
                  orderType === 'online'
                    ? `${ACCOUNT_ROUTES.OnlineOrderDetails.path}/${orderId}`
                    : `${ACCOUNT_ROUTES.StoreOrderDetails.path}/${orderId}`
                }
              />
            ),
        )}
        {isStoreOrdersLoadingFailed && (
          <HubPurchasesSingleMessage className={styles.isStoreOrdersLoadingFailed}>
            <Label copyKey="hub.store.purchase.not.loaded" />
          </HubPurchasesSingleMessage>
        )}
        {isOnlineOrdersLoadingFailed && (
          <HubPurchasesSingleMessage className={styles.isStoreOrdersLoadingFailed}>
            <Label copyKey="hub.purchase.not.loaded" />
          </HubPurchasesSingleMessage>
        )}
      </ul>
    )
  }

  const renderShoppingButton = (isVisible): any => (
    <a
      style={{
        transitionDelay: `${(hubOrderList.length + 1) * 75}ms`,
        opacity: Number(isVisible),
      }}
      className={styles.purchasesAction}
      href={EXTERNAL_ROUTES.homePage}
    >
      <BodyText Tag="span">
        <Label copyKey="hub.take.me.to.the.fashion" />
      </BodyText>
      <ChevronRightIcon className={styles.icon} />
    </a>
  )

  const renderPurchasesButton = (isVisible): any =>
    isMediumScreen ? null : (
      <Link
        style={{
          transitionDelay: `${(hubOrderList.length + 1) * 75}ms`,
          opacity: Number(isVisible),
        }}
        className={styles.purchasesAction}
        to={ACCOUNT_ROUTES.Purchases.path}
      >
        <Label copyKey="hub.view.all.purchase" />
        <ChevronRightIcon className={styles.icon} />
      </Link>
    )

  return (
    <TrackVisibilityOnMobile className={styles.container}>
      {({ isVisible }) => (
        <div
          className={cn(!isVisible ? styles.noOpacity : '', isNorthstarBrand && styles.addSpacing)}
        >
          <div className={styles.subHeadingWrapper} style={{ opacity: Number(isVisible) }}>
            {!isNorthstarBrand ? (
              <HeadingSmall>
                <Label copyKey="account.my.purchases.title" />
              </HeadingSmall>
            ) : (
              <>
                <div className={styles.viewAllButton}>
                  <HeadingLarge Tag="h2">
                    {getLabelFromStorage('profile.hmOrders.view.orders.ns')}
                  </HeadingLarge>
                  <CTALink href={ACCOUNT_ROUTES.Purchases.path} className={styles.hideUnderline}>
                    {getLabelFromStorage('account.view.all')}
                  </CTALink>
                </div>
                <hr className={styles.line} />
              </>
            )}
          </div>

          {renderOrders()}
          {isOrdersResultEmpty || isUserFailed
            ? renderShoppingButton(isVisible)
            : !isNorthstarBrand && renderPurchasesButton(isVisible)}
        </div>
      )}
    </TrackVisibilityOnMobile>
  )
}

export default HubPurchasesComponent
